/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { AgGridReact } from "@ag-grid-community/react";
import { themeQuartz } from "ag-grid-community"; 
import "./styles.scss";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { GridApi, ModuleRegistry } from "@ag-grid-community/core";
import { TableProps } from "./types";
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from "@mui/icons-material";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

export const Table = ({
  isLoading,
  rowsPerPage = 20,
  startPage = 1,
  colDefs = [],
  fetchGridData,
  fetchTotalInfo,
  onRowClick
}: TableProps) => {
  const [currentPage, setCurrentPage] = useState(startPage);
  const [totalRows, setTotalRows] = useState(0);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [rowData, setRowData] = useState<any[]>([]);
  const pageChangeInProgress = useRef(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch the total row count for the grid
  const fetchTotalRows = async () => {
    try {
      const totalData = await fetchTotalInfo();
      setTotalRows(totalData);
    } catch (error) {
      console.error("Error fetching total data:", error);
    }
  };

  const rowIndexes = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage + 1;
    const endIndex = Math.min(startIndex + rowsPerPage - 1, totalRows);
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    return { startIndex, endIndex, totalPages };
  }, [totalRows, rowsPerPage, currentPage]);

  useEffect(() => {
    fetchTotalRows();
  }, [gridApi]);

  // Function to fetch data for the current page
  const fetchPageData = async (pageNumber: number) => {
    if (!gridApi) {
      console.error("Grid API is not ready yet.");
      return;
    }

    const data = await fetchGridData(pageNumber, rowsPerPage);

    await setRowData(data);
    await setCurrentPage(pageNumber);
    setLoading(false);
  };

  const onPaginationChanged = (event: any) => {
    if (!gridApi || pageChangeInProgress.current) return;
    const newPage = event.api.paginationGetCurrentPage() + 1;
    if (newPage !== currentPage) {
      pageChangeInProgress.current = true;
      gridApi.paginationGoToPage(newPage - 1); // -1 because grid uses 0-indexed pages
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    if (gridApi && currentPage && !pageChangeInProgress.current) {
      fetchPageData(currentPage);
    }
  }, [gridApi, currentPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      await setRowData([]);
      setLoading(true);
      fetchPageData(page);
    }
  };

  const onGridReady = useCallback((params: any) => {
    setGridApi(params.api);
  }, []);

  const renderContent = () => {
    return (
      <AgGridReact
        theme={themeQuartz}
        loading={loading || isLoading}
        columnDefs={colDefs}
        rowData={rowData}
        rowBuffer={20}
        domLayout="autoHeight"
        headerHeight={48}
        rowHeight={48}
        onRowClicked={onRowClick}
        paginationPageSize={rowsPerPage}
        cacheBlockSize={rowsPerPage}
        maxConcurrentDatasourceRequests={1}
        maxBlocksInCache={200}
        onGridReady={onGridReady}
        pagination={true}
        onPaginationChanged={onPaginationChanged}
        suppressPaginationPanel={true}
        rowModelType="clientSide"
        overlayLoadingTemplate={`<span class="ag-overlay-loading-center">Loading...</span>`}
        overlayNoRowsTemplate={
          !loading && rowData.length === 0
            ? `<div class="custom-no-rows">No Rows To Show</div>`
            : ""
        }
      />
    );
  };
  return (
    <Box className={`h-full flex flex-col border border-[#A7ABAF] rounded-md relative mb-[48px] overflow-y-auto`}>
      <Box className="ag-theme-material flex-1 border-b">
        <Box className="h-full">
          {renderContent()} {}
        </Box>
      </Box>
      {!loading &&
        rowData.length > 0 && ( // Show pagination controls only if data is loaded
        <Box className="pagination-container p-2">
          <Typography id="row-count" className="mr-[38px]" data-testid="row-count">{rowIndexes.startIndex} to {rowIndexes.endIndex} of {totalRows}</Typography>

          <IconButton
            data-testid="first-arrow"
            onClick={() => {
              if (currentPage > 1) {
                const newPage = 1;
                handlePageChange(newPage);
              }
            }}
            disabled={currentPage === 1}
          >
            <FirstPage fontSize="medium" />
          </IconButton>
          {/* Back Arrow */}
          <IconButton
            data-testid="back-arrow"
            onClick={() => {
              if (currentPage > 1) {
                const newPage = currentPage - 1;
                handlePageChange(newPage);
              }
            }}
            disabled={currentPage === 1}
          >
            <KeyboardArrowLeft fontSize="medium" />
          </IconButton>

          <Typography id="page-count" data-testid="page-count">
            {`Page ${currentPage} of ${rowIndexes.totalPages}`}
          </Typography>

          {/* Forward Arrow */}
          <IconButton
            data-testid="forward-arrow"
            onClick={() => {
              if (currentPage < rowIndexes.totalPages) {
                const newPage = currentPage + 1;
                handlePageChange(newPage);
              }
            }}
            disabled={currentPage === rowIndexes.totalPages}
          >
            <KeyboardArrowRight fontSize="medium" />
          </IconButton>
          <IconButton
            data-testid="last-arrow"
            onClick={() => {
              if (currentPage < rowIndexes.totalPages) {
                const newPage = rowIndexes.totalPages;
                handlePageChange(newPage);
              }
            }}
            disabled={currentPage === rowIndexes.totalPages}
          >
            <LastPage fontSize="medium" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
